import { Group, Mesh, MeshBasicMaterial } from "three/webgpu";
import Experience from "../Experience";

// Forward convention: train moves toward +Z (engine faces +Z, boggies trail at -Z).
// Segments spawn far +Z (ahead) and scroll toward -Z (behind).
// Camera looks toward +Z, so its local +X maps to world -X. Lane 0 (player's left) sits at world +X.
export const LANE_X = [2.5, 0, -2.5];
export const SEG_LEN = 26;
const SEGMENTS_LIVE = 8;
const RECYCLE_BEHIND_Z = 20; // when seg.z < -RECYCLE_BEHIND_Z, recycle ahead
const RAIL_OFFSET_X = 0.4;
const RAIL_SCALE_X = 0.75;
const TRACK_MAT = new MeshBasicMaterial({ color: "#2e2e2e" });
const SPEED_BASE = 22;
const SPEED_MAX = 46;
const SPEED_RAMP_TIME = 90; // seconds to reach SPEED_MAX
// Visual length of each segment is slightly longer than its logical length so
// adjacent segments overlap at the boundary. The player sees both lanes' rails
// converging right at the jump point, giving a clear visual cue for when to
// press left/right.
const TRACK_OVERLAP = 10;
const RAIL_VISIBLE_LEN = SEG_LEN + TRACK_OVERLAP;

class TrackSegment {
  constructor(trackResource, scene) {
    this.scene = scene;
    this.lane = 1;

    this.group = new Group();

    this.railL = trackResource.scene.clone();
    this.railR = trackResource.scene.clone();

    const fitRail = (rail, side) => {
      rail.scale.set(RAIL_SCALE_X, 1, RAIL_VISIBLE_LEN / 6.5);
      rail.position.x = side * RAIL_OFFSET_X;
      rail.position.z = 0;
      rail.traverse((c) => {
        if (c instanceof Mesh) c.material = TRACK_MAT;
      });
    };
    fitRail(this.railL, -1);
    fitRail(this.railR, +1);

    this.group.add(this.railL);
    this.group.add(this.railR);

    this.scene.add(this.group);
  }

  setLane(idx) {
    this.lane = idx;
    this.group.position.x = LANE_X[idx];
  }

  setZ(z) {
    this.group.position.z = z;
  }

  get z() {
    return this.group.position.z;
  }

  dispose() {
    this.scene.remove(this.group);
  }
}

export default class TrackManager {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.time = this.experience.time;

    this.trackResource = this.resources.items.trackModel;

    // Pre-process source: reset inner mesh local positions so clones land on lane center.
    this.trackResource.scene.traverse((c) => {
      if (c instanceof Mesh) {
        c.material = TRACK_MAT;
        c.position.set(0, 0, 0);
      }
    });

    this.segments = [];
    this.speed = SPEED_BASE;
    this.runTime = 0;

    this._buildInitial();
  }

  _buildInitial() {
    // Tile segments stretching from just behind the train forward into +Z.
    for (let i = 0; i < SEGMENTS_LIVE; i++) {
      const seg = new TrackSegment(this.trackResource, this.scene);
      seg.setLane(1);
      // i=0 sits just behind the train, then progressing into +Z
      seg.setZ(-RECYCLE_BEHIND_Z + SEG_LEN + i * SEG_LEN);
      this.segments.push(seg);
    }
  }

  update(delta) {
    this.runTime += delta;
    const t = Math.min(1, this.runTime / SPEED_RAMP_TIME);
    this.speed = SPEED_BASE + (SPEED_MAX - SPEED_BASE) * t;
    const dz = this.speed * delta;
    for (const seg of this.segments) {
      seg.setZ(seg.z - dz);
      if (seg.z < -RECYCLE_BEHIND_Z) {
        const ref = this._furthestExcept(seg);
        const maxZ = ref ? ref.z : SEG_LEN;
        seg.setZ(maxZ + SEG_LEN);
        seg.setLane(this._chooseNextLane(ref));
      }
    }
  }

  _furthestExcept(except) {
    let furthest = null;
    for (const s of this.segments) {
      if (s === except) continue;
      if (!furthest || s.z > furthest.z) furthest = s;
    }
    return furthest;
  }

  _chooseNextLane(referenceSeg) {
    const refLane = referenceSeg ? referenceSeg.lane : 1;
    const choices = [refLane];
    if (refLane > 0) choices.push(refLane - 1);
    if (refLane < 2) choices.push(refLane + 1);
    return choices[Math.floor(Math.random() * choices.length)];
  }

  currentSegment() {
    for (const s of this.segments) {
      if (s.z >= -SEG_LEN / 2 && s.z < SEG_LEN / 2) return s;
    }
    return null;
  }

  nextSegment() {
    const cur = this.currentSegment();
    if (!cur) return null;
    let next = null;
    for (const s of this.segments) {
      if (s === cur) continue;
      if (s.z > cur.z && (!next || s.z < next.z)) next = s;
    }
    return next;
  }

  inputWindowOpen() {
    const cur = this.currentSegment();
    if (!cur) return false;
    // Open for the entire trailing half of the current segment — by then the
    // next segment's visual rails (extended by the track-overlap) are clearly
    // crossing the engine area, so the player has a confident cue to jump.
    return cur.z <= 0;
  }

  setSpeed(v) {
    this.speed = v;
  }

  getLaneAtZ(z) {
    for (const s of this.segments) {
      if (z >= s.z - SEG_LEN / 2 && z < s.z + SEG_LEN / 2) return s.lane;
    }
    return null;
  }

  getSegmentCenterAtZ(z) {
    for (const s of this.segments) {
      if (z >= s.z - SEG_LEN / 2 && z < s.z + SEG_LEN / 2) return s.z;
    }
    return null;
  }

  reset() {
    this.runTime = 0;
    this.speed = SPEED_BASE;
    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      seg.setLane(1);
      seg.setZ(-RECYCLE_BEHIND_Z + SEG_LEN + i * SEG_LEN);
    }
  }
}
